import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { MapPin, Clock, Users, Paperclip, Pencil, Trash2, X, Globe, Copy, Check, UploadCloud, UserPlus, Hourglass, Bell, Send, Download, FileText, Plus, Bus, BedDouble } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import GuestImportDialog from "@/components/GuestImportDialog";
import { InviteContactsDialog } from "@/pages/Contacts";
import AddGuestDialog from "@/components/AddGuestDialog";
import AddonsSelector from "@/components/AddonsSelector";
import { TRANSPORT_LABEL, prettyDate } from "@/lib/addons";
import { BACKEND_URL } from "@/lib/api";

const VisuallyHidden = ({ children }) => (
    <span style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", borderWidth: 0 }}>
        {children}
    </span>
);

export default function ActivityDrawer({ activityId, open, onOpenChange, onChanged, onEdit }) {
    const { user } = useAuth();
    const [activity, setActivity] = useState(null);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [importGuestsOpen, setImportGuestsOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [addGuestOpen, setAddGuestOpen] = useState(false);
    const [transportChoice, setTransportChoice] = useState(null);
    const [accommodationDates, setAccommodationDates] = useState([]);

    useEffect(() => {
        if (!open || !activityId) return;
        setNote("");
        setTransportChoice(null);
        setAccommodationDates([]);
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/activities/${activityId}`);
                if (alive) setActivity(data);
            } catch {
                toast.error("Could not load activity");
            }
        })();
        return () => { alive = false; };
    }, [activityId, open]);

    if (!activity) {
        return (
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className="w-full sm:max-w-lg rounded-none border-l-[#E5E5E3] p-0" data-testid="activity-drawer-loading">
                    <VisuallyHidden><SheetTitle>Loading activity</SheetTitle></VisuallyHidden>
                    <div className="p-8 text-sm text-[#666664]">Loading…</div>
                </SheetContent>
            </Sheet>
        );
    }

    const isCreator = user?.user_id === activity.created_by;
    const myStatus = [...activity.signups, ...activity.waitlist].find(s => s.user_id === user?.user_id)?.status;
    const max = activity.max_signups || 0;
    const isFull = max > 0 && activity.signup_count >= max;
    const isPast = !!activity.is_past;
    const isClosed = !!activity.is_closed;

    const signup = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/activities/${activity.id}/signup`, {
                note,
                transport_choice: transportChoice,
                accommodation_dates: accommodationDates,
            });
            setActivity(data);
            setNote("");
            setTransportChoice(null);
            setAccommodationDates([]);
            onChanged?.();
            toast.success(isFull ? "Added to waitlist" : "Signed up");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to sign up");
        } finally {
            setBusy(false);
        }
    };

    const cancel = async () => {
        setBusy(true);
        try {
            const { data } = await api.delete(`/activities/${activity.id}/signup`);
            setActivity(data);
            onChanged?.();
            toast.success("Cancelled sign-up");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to cancel");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        setBusy(true);
        try {
            await api.delete(`/activities/${activity.id}`);
            setConfirmOpen(false);
            onChanged?.();
            onOpenChange(false);
            toast.success("Activity deleted");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to delete");
        } finally {
            setBusy(false);
        }
    };

    const removeAttendee = async (s) => {
        const who = s.user?.name || s.user?.email || "this attendee";
        if (!window.confirm(`Remove ${who} from the RSVP list?`)) return;
        try {
            const url = s.is_guest
                ? `/activities/${activity.id}/guests/${s.id}`
                : `/activities/${activity.id}/signups/${s.id}`;
            const { data } = await api.delete(url);
            setActivity(data.activity);
            onChanged?.();
            toast.success("Removed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to remove");
        }
    };

    const exportFile = (kind) => {
        const a = document.createElement("a");
        a.href = `${BACKEND_URL}/api/activities/${activity.id}/export.${kind}`;
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
    };

    return (
        <>
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                className="w-full sm:max-w-lg rounded-none border-l-[#E5E5E3] p-0 flex flex-col gap-0"
                data-testid="activity-drawer"
            >
                <div className="border-b border-[#E5E5E3] p-8 pt-10" style={{ borderTop: `4px solid ${activity.color || "#F0F0EE"}` }}>
                    {activity.tag && (
                        <span className="inline-block text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-3" data-testid="activity-tag">
                            {activity.tag}
                        </span>
                    )}
                    <SheetHeader className="p-0 text-left space-y-2">
                        <SheetTitle className="font-heading text-3xl font-light tracking-tight text-[#111110] leading-tight" data-testid="activity-title">
                            {activity.title}
                        </SheetTitle>
                        <SheetDescription className="text-sm text-[#666664]" data-testid="activity-creator">
                            Created by <span className="text-[#111110]">{activity.creator?.name || activity.creator?.email}</span>
                        </SheetDescription>
                    </SheetHeader>

                    <div className="mt-6 space-y-3">
                        <InfoRow icon={<Clock className="w-4 h-4" strokeWidth={1.5} />}>
                            <span data-testid="activity-time">
                                {format(new Date(activity.start_time), "EEE, MMM d · h:mm a")}
                                {" — "}
                                {format(new Date(activity.end_time), "h:mm a")}
                            </span>
                        </InfoRow>
                        {activity.location && (
                            <InfoRow icon={<MapPin className="w-4 h-4" strokeWidth={1.5} />}>
                                <span data-testid="activity-location">{activity.location}</span>
                            </InfoRow>
                        )}
                        <InfoRow icon={<Users className="w-4 h-4" strokeWidth={1.5} />}>
                            <span data-testid="activity-capacity">
                                {activity.signup_count}
                                {max > 0 ? ` / ${max}` : ""} signed up
                                {activity.waitlist_count > 0 && ` · ${activity.waitlist_count} on waitlist`}
                            </span>
                        </InfoRow>
                        {activity.signup_deadline && (
                            <InfoRow icon={<Hourglass className="w-4 h-4" strokeWidth={1.5} />}>
                                <span data-testid="activity-deadline" className={isClosed ? "text-[#999996]" : undefined}>
                                    {isPast ? "Event ended " : isClosed ? "Sign-ups closed " : "RSVP by "}
                                    {format(new Date(activity.signup_deadline), "EEE, MMM d · h:mm a")}
                                </span>
                            </InfoRow>
                        )}
                        {!activity.signup_deadline && isPast && (
                            <InfoRow icon={<Hourglass className="w-4 h-4" strokeWidth={1.5} />}>
                                <span data-testid="activity-ended" className="text-[#999996]">
                                    Event ended {format(new Date(activity.end_time), "EEE, MMM d · h:mm a")}
                                </span>
                            </InfoRow>
                        )}
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-8 space-y-8">
                    {activity.description && (
                        <section>
                            <Label>Description</Label>
                            <p className="text-[#333333] text-[15px] leading-relaxed whitespace-pre-wrap" data-testid="activity-description">
                                {activity.description}
                            </p>
                        </section>
                    )}

                    {activity.attachments?.length > 0 && (
                        <section>
                            <Label>Attachments</Label>
                            <ul className="space-y-2">
                                {activity.attachments.map(a => (
                                    <li key={a.id} className="border border-[#E5E5E3] p-3 flex items-center gap-3 text-sm">
                                        <Paperclip className="w-4 h-4 text-[#666664]" strokeWidth={1.5} />
                                        <a
                                            href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.storage_path}`}
                                            target="_blank" rel="noreferrer"
                                            className="text-[#111110] hover:underline truncate flex-1"
                                            data-testid={`attachment-${a.id}`}
                                        >
                                            {a.original_filename}
                                        </a>
                                        <span className="text-xs text-[#888885]">{formatSize(a.size)}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {(activity.offer_transport || activity.offer_accommodation) && (
                        <AddonsSummary activity={activity} />
                    )}

                    {activity.is_public && (
                        <section data-testid="public-link-section">
                            <Label>Public RSVP link</Label>
                            <PublicLinkBlock activityId={activity.id} />
                        </section>
                    )}

                    <section>
                        <div className="flex items-baseline justify-between mb-3">
                            <Label className="mb-0">Attendees</Label>
                            <span className="text-xs text-[#888885]" data-testid="attendees-count">{activity.signup_count}</span>
                        </div>
                        {activity.signups.length === 0 ? (
                            <EmptyRow>Be the first to sign up.</EmptyRow>
                        ) : (
                            <ul className="divide-y divide-[#E5E5E3] border-t border-b border-[#E5E5E3]" data-testid="signups-list">
                                {activity.signups.map(s => (
                                    <SignupRow key={s.id} s={s} canRemove={isCreator}
                                        onRemove={() => removeAttendee(s)} />
                                ))}
                            </ul>
                        )}
                    </section>

                    {activity.waitlist.length > 0 && (
                        <section>
                            <div className="flex items-baseline justify-between mb-3">
                                <Label className="mb-0">Waitlist</Label>
                                <span className="text-xs text-[#888885]" data-testid="waitlist-count">{activity.waitlist_count}</span>
                            </div>
                            <ul className="divide-y divide-[#E5E5E3] border-t border-b border-[#E5E5E3]" data-testid="waitlist-list">
                                {activity.waitlist.map(s => (
                                    <SignupRow key={s.id} s={s} waitlist canRemove={isCreator}
                                        onRemove={() => removeAttendee(s)} />
                                ))}
                            </ul>
                        </section>
                    )}

                    {isCreator && (
                        <ReminderSection activity={activity} onRefresh={(updated) => { setActivity(updated); onChanged?.(); }} />
                    )}

                    {isCreator && (
                        <section className="pt-2 space-y-2">
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    className="rounded-none border-[#E5E5E3] h-10 flex-1 text-[#111110] hover:bg-[#F0F0EE]"
                                    onClick={() => setAddGuestOpen(true)}
                                    disabled={isClosed}
                                    data-testid="add-guest-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add guest
                                </Button>
                                <Button
                                    variant="outline"
                                    className="rounded-none border-[#E5E5E3] h-10 flex-1 text-[#111110] hover:bg-[#F0F0EE]"
                                    onClick={() => setInviteOpen(true)}
                                    disabled={isClosed}
                                    data-testid="invite-contacts-btn"
                                >
                                    <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Invite contacts
                                </Button>
                                <Button
                                    variant="outline"
                                    className="rounded-none border-[#E5E5E3] h-10 flex-1 text-[#111110] hover:bg-[#F0F0EE]"
                                    onClick={() => setImportGuestsOpen(true)}
                                    disabled={isClosed}
                                    data-testid="import-guests-btn"
                                >
                                    <UploadCloud className="w-4 h-4 mr-2" strokeWidth={1.5} /> Import CSV
                                </Button>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    className="rounded-none border-[#E5E5E3] h-10 flex-1 text-[#111110] hover:bg-[#F0F0EE]"
                                    onClick={() => exportFile("csv")}
                                    data-testid="export-csv-btn"
                                >
                                    <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Export CSV
                                </Button>
                                <Button
                                    variant="outline"
                                    className="rounded-none border-[#E5E5E3] h-10 flex-1 text-[#111110] hover:bg-[#F0F0EE]"
                                    onClick={() => exportFile("pdf")}
                                    data-testid="export-pdf-btn"
                                >
                                    <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} /> Export PDF
                                </Button>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    className="rounded-none border-[#E5E5E3] h-10 flex-1 text-[#111110] hover:bg-[#F0F0EE]"
                                    onClick={() => onEdit?.(activity)}
                                    data-testid="edit-activity-btn"
                                >
                                    <Pencil className="w-4 h-4 mr-2" strokeWidth={1.5} /> Edit
                                </Button>
                                <Button
                                    variant="outline"
                                    className="rounded-none border-[#E5E5E3] h-10 flex-1 text-[#111110] hover:bg-[#F0F0EE]"
                                    onClick={() => setConfirmOpen(true)}
                                    disabled={busy}
                                    data-testid="delete-activity-btn"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Delete
                                </Button>
                            </div>
                        </section>
                    )}
                </div>

                {/* Sticky bottom action */}
                <div className="border-t border-[#E5E5E3] p-6 bg-white">
                    {myStatus ? (
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-sm text-[#666664]" data-testid="my-status">
                                {myStatus === "waitlist" ? "You're on the waitlist." : "You're signed up."}
                            </div>
                            <Button
                                variant="outline"
                                className="rounded-none border-[#E5E5E3] h-10 px-5 text-[#111110] hover:bg-[#F0F0EE]"
                                onClick={cancel}
                                disabled={busy}
                                data-testid="cancel-signup-btn"
                            >
                                <X className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Cancel
                            </Button>
                        </div>
                    ) : isPast ? (
                        <div className="text-center py-2" data-testid="ended-notice">
                            <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-1">Event ended</p>
                            <p className="text-sm text-[#666664]">This activity is in the past.</p>
                        </div>
                    ) : isClosed ? (
                        <div className="text-center py-2" data-testid="closed-notice">
                            <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-1">Registration closed</p>
                            <p className="text-sm text-[#666664]">The deadline has passed. Reach out to the host directly if you still want to attend.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <AddonsSelector
                                activity={activity}
                                transportChoice={transportChoice}
                                onTransportChoice={setTransportChoice}
                                accommodationDates={accommodationDates}
                                onAccommodationDates={setAccommodationDates}
                                idPrefix="signup"
                            />
                            <Textarea
                                placeholder="Optional note (e.g. dietary preferences, arriving late)"
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                className="rounded-none border-[#E5E5E3] resize-none min-h-[64px] focus-visible:ring-1 focus-visible:ring-[#111110]"
                                data-testid="signup-note-input"
                            />
                            <Button
                                className="w-full h-11 rounded-none bg-[#111110] hover:bg-[#333333] text-white font-medium"
                                onClick={signup}
                                disabled={busy}
                                data-testid="signup-btn"
                            >
                                {isFull ? "Join waitlist" : "Sign up"}
                            </Button>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent className="rounded-none border-[#E5E5E3] shadow-none" data-testid="delete-confirm-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle className="font-heading text-2xl font-light tracking-tight text-[#111110]">
                        Delete this activity?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-sm text-[#666664]">
                        &quot;{activity.title}&quot; will be removed for everyone, along with all sign-ups and waitlist entries. This cannot be undone.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="gap-2">
                    <AlertDialogCancel
                        className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE] mt-0"
                        data-testid="delete-cancel-btn"
                    >
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={remove}
                        disabled={busy}
                        className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-6"
                        data-testid="delete-confirm-btn"
                    >
                        {busy ? "Deleting…" : "Delete activity"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
        <GuestImportDialog
            open={importGuestsOpen}
            onOpenChange={setImportGuestsOpen}
            activityId={activity.id}
            onImported={(updated) => { setActivity(updated); onChanged?.(); }}
        />
        <InviteContactsDialog
            open={inviteOpen}
            onOpenChange={setInviteOpen}
            activityId={activity.id}
            onInvited={(updated) => { setActivity(updated); onChanged?.(); }}
        />
        <AddGuestDialog
            open={addGuestOpen}
            onOpenChange={setAddGuestOpen}
            activityId={activity.id}
            onAdded={(updated) => { setActivity(updated); onChanged?.(); }}
        />
        </>
    );
}

function InfoRow({ icon, children }) {
    return (
        <div className="flex items-center gap-3 text-sm text-[#333333]">
            <span className="text-[#666664]">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

function Label({ children, className = "" }) {
    return <p className={`text-[10px] tracking-[0.2em] uppercase text-[#888885] font-medium mb-3 ${className}`}>{children}</p>;
}

function EmptyRow({ children }) {
    return <p className="text-sm text-[#888885] border border-dashed border-[#E5E5E3] p-6 text-center">{children}</p>;
}

function SignupRow({ s, waitlist, canRemove, onRemove }) {
    const initials = (s.user?.name || s.user?.email || "?").split(" ").map(x => x[0]).slice(0,2).join("").toUpperCase();
    const nights = s.accommodation_dates || [];
    return (
        <li className="py-3 flex items-start gap-3" data-testid={`signup-${s.id}`}>
            <Avatar className="w-8 h-8 rounded-none border border-[#E5E5E3]">
                <AvatarImage src={s.user?.picture} />
                <AvatarFallback className="rounded-none bg-[#F0F0EE] text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <p className="text-sm text-[#111110] font-medium truncate">
                        {s.user?.name || s.user?.email}
                        {s.is_guest && <span className="ml-2 text-[10px] tracking-[0.15em] uppercase text-[#888885] border border-[#E5E5E3] px-1.5 py-0.5">Guest</span>}
                    </p>
                    {waitlist && <Badge variant="outline" className="rounded-none border-[#E5E5E3] text-[10px] tracking-[0.15em] uppercase text-[#888885]">Waitlist</Badge>}
                </div>
                {s.is_guest && (
                    <p className="text-xs text-[#888885] mt-0.5">{s.user?.email}{s.user?.phone ? ` · ${s.user.phone}` : ""}</p>
                )}
                {(s.transport_choice || nights.length > 0) && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`signup-addons-${s.id}`}>
                        {s.transport_choice && (
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase text-[#666664] border border-[#E5E5E3] px-1.5 py-0.5">
                                <Bus className="w-3 h-3" strokeWidth={1.5} />
                                {TRANSPORT_LABEL[s.transport_choice] || s.transport_choice}
                            </span>
                        )}
                        {nights.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase text-[#666664] border border-[#E5E5E3] px-1.5 py-0.5">
                                <BedDouble className="w-3 h-3" strokeWidth={1.5} />
                                {nights.length} night{nights.length === 1 ? "" : "s"}
                            </span>
                        )}
                    </div>
                )}
                {s.note && <p className="text-xs text-[#666664] mt-0.5 whitespace-pre-wrap">{s.note}</p>}
            </div>
            {canRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label="Remove from RSVP list"
                    title="Remove"
                    data-testid={`remove-attendee-${s.id}`}
                    className="text-[#888885] hover:text-[#111110] p-1.5 -mr-1 shrink-0"
                >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
            )}
        </li>
    );
}

function AddonsSummary({ activity }) {
    const t = activity.transport_summary || {};
    const a = activity.accommodation_summary || {};
    return (
        <section data-testid="addons-summary">
            <Label>Add-ons summary</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activity.offer_transport && (
                    <div className="border border-[#E5E5E3] p-4" data-testid="transport-summary">
                        <div className="flex items-center gap-2 mb-3">
                            <Bus className="w-4 h-4 text-[#666664]" strokeWidth={1.5} />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-[#888885] font-medium">Transportation</span>
                        </div>
                        <ul className="space-y-1.5 text-sm">
                            {(activity.transport_options || []).map(k => (
                                <li key={k} className="flex items-center justify-between gap-3" data-testid={`transport-summary-${k}`}>
                                    <span className="text-[#333333]">{TRANSPORT_LABEL[k] || k}</span>
                                    <span className="text-[#111110] font-medium tabular-nums">{t[k] || 0}</span>
                                </li>
                            ))}
                            <li className="flex items-center justify-between gap-3 pt-1.5 border-t border-[#E5E5E3] text-xs text-[#888885]">
                                <span>None / opted out</span>
                                <span className="tabular-nums">{activity.transport_none_count || 0}</span>
                            </li>
                        </ul>
                    </div>
                )}
                {activity.offer_accommodation && (
                    <div className="border border-[#E5E5E3] p-4" data-testid="accommodation-summary">
                        <div className="flex items-center gap-2 mb-3">
                            <BedDouble className="w-4 h-4 text-[#666664]" strokeWidth={1.5} />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-[#888885] font-medium">Accommodation nights</span>
                        </div>
                        <ul className="space-y-1.5 text-sm">
                            {(activity.accommodation_dates || []).map(d => (
                                <li key={d} className="flex items-center justify-between gap-3" data-testid={`accommodation-summary-${d}`}>
                                    <span className="text-[#333333]">
                                        {prettyDate(d)}
                                        <span className="text-[10px] text-[#888885] font-mono ml-2">{d}</span>
                                    </span>
                                    <span className="text-[#111110] font-medium tabular-nums">{a[d] || 0}</span>
                                </li>
                            ))}
                            <li className="flex items-center justify-between gap-3 pt-1.5 border-t border-[#E5E5E3] text-xs text-[#888885]">
                                <span>None / opted out</span>
                                <span className="tabular-nums">{activity.accommodation_none_count || 0}</span>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </section>
    );
}

function PublicLinkBlock({ activityId }) {
    const url = `${window.location.origin}/public/${activityId}`;
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Could not copy");
        }
    };
    return (
        <div className="border border-[#E5E5E3] flex items-center" data-testid="public-link-block">
            <span className="px-3 py-2.5 text-[#666664]"><Globe className="w-4 h-4" strokeWidth={1.5} /></span>
            <input
                readOnly
                value={url}
                onClick={e => e.target.select()}
                data-testid="public-link-input"
                className="flex-1 bg-transparent border-none outline-none text-sm text-[#111110] font-mono py-2 pr-2 truncate"
            />
            <button onClick={copy} data-testid="copy-public-link-btn" className="px-3 h-10 border-l border-[#E5E5E3] text-xs tracking-[0.15em] uppercase font-medium text-[#111110] hover:bg-[#F0F0EE] inline-flex items-center gap-1.5">
                {copied ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />}
                {copied ? "Copied" : "Copy"}
            </button>
        </div>
    );
}

function formatSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function ReminderSection({ activity, onRefresh }) {
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [preview, setPreview] = useState(null); // {recipients: []}

    const scheduled = activity.reminder_at ? new Date(activity.reminder_at) : null;
    const lastSent = activity.reminder_last_sent_at ? new Date(activity.reminder_last_sent_at) : null;
    const isDue = scheduled && new Date() >= scheduled && (!lastSent || lastSent < scheduled);

    const openPreview = async () => {
        setBusy(true);
        try {
            const { data } = await api.get(`/activities/${activity.id}/reminder/recipients`);
            setPreview(data);
            setConfirmOpen(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load recipients");
        } finally {
            setBusy(false);
        }
    };

    const dispatch = async () => {
        if (!preview?.recipients?.length) return toast.error("No active attendees to remind yet.");
        setBusy(true);
        try {
            const { data } = await api.post(`/activities/${activity.id}/reminder/dispatch`);
            onRefresh?.(data.activity);
            // Open the default mail client pre-populated with BCC + subject + body
            const startStr = format(new Date(activity.start_time), "EEE, MMM d · h:mm a");
            const subject = `Reminder: ${activity.title} — ${startStr}`;
            const body = [
                `Hi,`,
                ``,
                `Quick reminder about "${activity.title}":`,
                `📅 ${startStr}`,
                activity.location ? `📍 ${activity.location}` : null,
                ``,
                activity.description || "",
                ``,
                `— Scratchfirst Calendar`,
            ].filter(Boolean).join("\n");
            const emails = data.reminder.recipients.map(r => r.email).join(",");
            const href = `mailto:?bcc=${encodeURIComponent(emails)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            window.location.href = href;
            toast.success(`Reminder dispatched to ${data.reminder.recipient_count} attendee${data.reminder.recipient_count === 1 ? "" : "s"}`);
            setConfirmOpen(false);
            setPreview(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Dispatch failed");
        } finally {
            setBusy(false);
        }
    };

    const copyEmails = async () => {
        const emails = (preview?.recipients || []).map(r => r.email).join(", ");
        try {
            await navigator.clipboard.writeText(emails);
            toast.success("Email list copied");
        } catch {
            toast.error("Could not copy");
        }
    };

    return (
        <section className="border border-[#E5E5E3] p-5 space-y-3" data-testid="reminder-section">
            <div className="flex items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2">
                    <Bell className="w-4 h-4 text-[#666664]" strokeWidth={1.5} />
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">Reminder</span>
                </div>
                {isDue && <span className="text-[10px] tracking-[0.2em] uppercase text-[#111110] border border-[#111110] px-2 py-0.5" data-testid="reminder-due-badge">Due</span>}
            </div>
            <div className="text-sm text-[#333333]" data-testid="reminder-status">
                {scheduled ? (
                    <>
                        Scheduled for <span className="text-[#111110] font-medium">{format(scheduled, "EEE, MMM d · h:mm a")}</span>
                    </>
                ) : (
                    <span className="text-[#666664]">No reminder scheduled. You can still send one now.</span>
                )}
                {lastSent && (
                    <p className="text-xs text-[#888885] mt-1" data-testid="reminder-last-sent">
                        Last sent {format(lastSent, "EEE, MMM d · h:mm a")}
                    </p>
                )}
            </div>
            <Button
                onClick={openPreview}
                disabled={busy}
                className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-5 font-medium"
                data-testid="send-reminder-btn"
            >
                <Send className="w-4 h-4 mr-2" strokeWidth={1.5} /> Send reminder now
            </Button>

            <AlertDialog open={confirmOpen} onOpenChange={(v) => { if (!v) setPreview(null); setConfirmOpen(v); }}>
                <AlertDialogContent className="rounded-none border-[#E5E5E3] shadow-none max-w-lg" data-testid="reminder-preview-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading text-2xl font-light tracking-tight text-[#111110]">Send reminder to {preview?.recipients?.length ?? 0} attendee{preview?.recipients?.length === 1 ? "" : "s"}?</AlertDialogTitle>
                        <AlertDialogDescription className="text-sm text-[#666664]">
                            We&apos;ll open your default mail client with everyone pre-filled in <span className="text-[#111110]">BCC</span> (privacy-safe) and a short reminder in the body. You can edit before sending.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="border border-[#E5E5E3] max-h-48 overflow-y-auto" data-testid="reminder-recipients-list">
                        {(preview?.recipients || []).length === 0 ? (
                            <p className="p-4 text-sm text-[#888885] text-center">No confirmed attendees yet.</p>
                        ) : (
                            <ul className="divide-y divide-[#E5E5E3]">
                                {preview.recipients.map(r => (
                                    <li key={r.email} className="p-3 flex items-center justify-between gap-3 text-sm" data-testid={`reminder-recipient-${r.email}`}>
                                        <span className="text-[#111110] truncate">{r.name}</span>
                                        <span className="text-[#666664] font-mono text-xs truncate">{r.email}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <AlertDialogFooter className="gap-2 flex-row justify-end">
                        <AlertDialogCancel
                            className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE] mt-0"
                            data-testid="reminder-cancel-btn"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={copyEmails}
                            disabled={!preview?.recipients?.length}
                            className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE]"
                            data-testid="reminder-copy-btn"
                        >
                            <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} /> Copy emails
                        </Button>
                        <AlertDialogAction
                            onClick={dispatch}
                            disabled={busy || !preview?.recipients?.length}
                            className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-6"
                            data-testid="reminder-dispatch-btn"
                        >
                            <Send className="w-4 h-4 mr-2" strokeWidth={1.5} /> Open mail app
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}
